// Copyright (c) Ping. All rights reserved.
//
// Send-side builders for the meeting AppEvents (types-and-schemas.md §2.13,
// 0xC2–0xC5). These produce the `payload` object for `encodeEnvelopeJsonBytes`,
// with snake_case keys and optional fields OMITTED (never JSON null) so the
// bytes match iOS `MeetingAppEvent.encodeJSONBytes()` exactly. The UI hands the
// result to the host's `conv.send` — same contract as the call `build*` helpers.

import type {
  MeetingAutoCall,
  MeetingRsvpResponse,
} from '../appEvent/types';

/** 16 random bytes as 32 lowercase hex chars — the canonical `meeting_id`
 *  (same convention as call_id / task_id / note_id). */
export function newMeetingId(): string {
  const bytes = new Uint8Array(16);
  (globalThis.crypto ?? crypto).getRandomValues(bytes);
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return hex;
}

export interface MeetingCreateInput {
  readonly meetingId: string;
  readonly title: string;
  readonly startsAt: number;
  readonly endsAt: number;
  readonly attendeeUserIds?: ReadonlyArray<string>;
  readonly location?: string;
  readonly description?: string;
  readonly meetingLink?: string;
  readonly autoCall?: MeetingAutoCall;
}

export function buildMeetingCreatePayload(input: MeetingCreateInput): Record<string, unknown> {
  const p: Record<string, unknown> = {
    meeting_id: input.meetingId,
    title: input.title,
    starts_at: input.startsAt,
    ends_at: input.endsAt,
    attendee_user_ids: input.attendeeUserIds ?? [],
  };
  if (nonEmpty(input.location)) p.location = input.location;
  if (nonEmpty(input.description)) p.description = input.description;
  if (nonEmpty(input.meetingLink)) p.meeting_link = input.meetingLink;
  if (input.autoCall !== undefined) {
    p.auto_call = {
      media: input.autoCall.media,
      pre_invite_minutes: input.autoCall.pre_invite_minutes,
    };
  }
  return p;
}

/** Partial diff off the ORIGINAL record. Only changed fields are sent; passing
 *  an empty string for location/description/meetingLink CLEARS it on peers. */
export interface MeetingEditInput {
  readonly meetingId: string;
  readonly title?: string;
  readonly startsAt?: number;
  readonly endsAt?: number;
  readonly location?: string; // '' clears
  readonly description?: string; // '' clears
  readonly meetingLink?: string; // '' clears
  readonly attendeeAdded?: ReadonlyArray<string>;
  readonly attendeeRemoved?: ReadonlyArray<string>;
}

export function buildMeetingUpdatePayload(input: MeetingEditInput): Record<string, unknown> {
  const p: Record<string, unknown> = { meeting_id: input.meetingId };
  if (input.title !== undefined) p.title = input.title;
  if (input.startsAt !== undefined) p.starts_at = input.startsAt;
  if (input.endsAt !== undefined) p.ends_at = input.endsAt;
  if (input.location !== undefined) p.location = input.location; // keep '' (clear)
  if (input.description !== undefined) p.description = input.description;
  if (input.meetingLink !== undefined) p.meeting_link = input.meetingLink;
  if (input.attendeeAdded !== undefined && input.attendeeAdded.length > 0) {
    p.attendee_user_ids_added = [...input.attendeeAdded];
  }
  if (input.attendeeRemoved !== undefined && input.attendeeRemoved.length > 0) {
    p.attendee_user_ids_removed = [...input.attendeeRemoved];
  }
  return p;
}

export function buildMeetingRsvpPayload(
  meetingId: string,
  response: MeetingRsvpResponse,
  note?: string,
): Record<string, unknown> {
  const p: Record<string, unknown> = { meeting_id: meetingId, response };
  if (nonEmpty(note)) p.note = note;
  return p;
}

export function buildMeetingCancelPayload(
  meetingId: string,
  reason?: string,
): Record<string, unknown> {
  const p: Record<string, unknown> = { meeting_id: meetingId };
  if (nonEmpty(reason)) p.reason = reason;
  return p;
}

function nonEmpty(s: string | undefined): s is string {
  return typeof s === 'string' && s.length > 0;
}
